import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import cartService from '../services/cartService';
import { useAuthStore } from './authStore';

export const useCartStore = create()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      isSyncing: false,
      lastSyncedAt: null,

      /**
       * Add item to cart
       * - If authenticated: sync with backend
       * - If guest: store locally
       */
      addItem: async (product, quantity = 1) => {
        if (!product) return;
        const productId = product._id || product.id;
        if (!productId) return;

        const qty = Number(quantity) > 0 ? Math.floor(Number(quantity)) : 1;
        const maxStock = typeof product.stock === 'number' && product.stock >= 0 ? product.stock : 9999;
        if (maxStock === 0) return;

        const isAuthenticated = useAuthStore.getState().isAuthenticated;

        // Optimistic update
        set((state) => {
          const existing = state.items.find((i) => i.productId === productId);
          if (existing) {
            const currentQty = Number(existing.quantity) || 0;
            const updatedQty = Math.min(currentQty + qty, maxStock);
            return {
              items: state.items.map((i) =>
                i.productId === productId
                  ? { ...i, quantity: updatedQty }
                  : i
              ),
            };
          }

          const initialQty = Math.min(qty, maxStock);
          const normalizedProduct = {
            ...product,
            _id: productId,
            price: typeof product.price === 'number'
              ? product.price
              : (typeof product.displayPrice === 'number' ? product.displayPrice : 0),
            stock: maxStock,
            images: Array.isArray(product.images) && product.images.length > 0
              ? product.images
              : (product.image ? [product.image] : ['/images/placeholder.png']),
            name: product.name || 'Product',
          };

          return {
            items: [
              ...state.items,
              {
                productId,
                product: normalizedProduct,
                quantity: initialQty,
              },
            ],
          };
        });

        // Sync with backend if authenticated
        if (isAuthenticated) {
          try {
            set({ isSyncing: true });
            await cartService.addToCart({
              product: productId,
              quantity: qty,
            });
            set({ lastSyncedAt: Date.now(), isSyncing: false });
          } catch (error) {
            console.error('Failed to sync cart with backend:', error);
            set({ isSyncing: false });
            // Keep optimistic update even if sync fails
          }
        }
      },

      /**
       * Remove item from cart
       */
      removeItem: async (productId, cartItemId) => {
        const isAuthenticated = useAuthStore.getState().isAuthenticated;
        const matches = (item) => cartItemId
          ? item.cartItemId === cartItemId
          : item.productId === productId;
        const removedItems = get().items.filter(matches);

        // Optimistic update
        set((state) => ({
          items: state.items.filter((i) => !matches(i)),
        }));

        // Sync with backend if authenticated
        if (isAuthenticated && removedItems.length) {
          try {
            set({ isSyncing: true });
            // Find backend cart item ID
            let ids = cartItemId ? [cartItemId] : [];
            if (!cartItemId) {
              const backendCart = await cartService.getCart();
              ids = backendCart.cart.items.filter(
                (i) => (typeof i.product === 'string' ? i.product : i.product?._id) === productId
              ).map((i) => i._id);
            }
            for (const id of ids) {
              await cartService.removeCartItem(id);
            }
            set({ lastSyncedAt: Date.now(), isSyncing: false });
          } catch (error) {
            console.error('Failed to sync cart removal with backend:', error);
            set((state) => ({
              items: [...state.items, ...removedItems.filter((item) =>
                !state.items.some((current) => item.cartItemId
                  ? current.cartItemId === item.cartItemId
                  : current.productId === item.productId))],
              isSyncing: false,
            }));
            throw error;
          }
        }
      },

      /**
       * Update item quantity
       */
      updateQuantity: async (productId, quantity, cartItemId) => {
        const qty = Number(quantity);
        if (isNaN(qty) || qty <= 0) {
          await get().removeItem(productId, cartItemId);
          return;
        }

        const validQty = Math.floor(qty);
        const isAuthenticated = useAuthStore.getState().isAuthenticated;

        // Optimistic update
        set((state) => ({
          items: state.items.map((i) => {
            if (cartItemId ? i.cartItemId === cartItemId : i.productId === productId) {
              const maxStock = typeof i.product?.stock === 'number' && i.product.stock >= 0 ? i.product.stock : 9999;
              return { ...i, quantity: Math.min(validQty, maxStock) };
            }
            return i;
          }),
        }));

        // Sync with backend if authenticated
        if (isAuthenticated) {
          try {
            set({ isSyncing: true });
            // Find backend cart item ID
            const backendCart = await cartService.getCart();
            const backendItem = backendCart.cart.items.find(
              (i) => cartItemId ? i._id === cartItemId : (typeof i.product === 'string' ? i.product : i.product?._id) === productId
            );
            
            if (backendItem && backendItem._id) {
              await cartService.updateCartItem(backendItem._id, { quantity: validQty });
            }
            set({ lastSyncedAt: Date.now(), isSyncing: false });
          } catch (error) {
            console.error('Failed to sync cart update with backend:', error);
            set({ isSyncing: false });
          }
        }
      },

      /**
       * Clear cart
       */
      clearCart: async () => {
        const isAuthenticated = useAuthStore.getState().isAuthenticated;

        // Optimistic update
        set({ items: [] });

        // Sync with backend if authenticated
        if (isAuthenticated) {
          try {
            set({ isSyncing: true });
            await cartService.clearCart();
            set({ lastSyncedAt: Date.now(), isSyncing: false });
          } catch (error) {
            console.error('Failed to clear cart on backend:', error);
            set({ isSyncing: false });
          }
        }
      },

      /**
       * Fetch cart from backend
       * - Called on login or page load if authenticated
       */
      fetchCart: async () => {
        const itemsBeforeFetch = get().items;
        const isAuthenticated = useAuthStore.getState().isAuthenticated;
        
        if (!isAuthenticated) {
          return; // Guest user - keep local cart
        }

        try {
          set({ isSyncing: true });
          const response = await cartService.getCart();
          
          // Transform backend cart items to local format
          const items= response.cart.items.map((item) => ({
            cartItemId: item._id,
            productId: typeof item.product === 'string' ? item.product : item.product._id,
            product: typeof item.product === 'string' 
              ? {
                  _id: item.product,
                  name: item.name,
                  images: [item.image],
                  price: item.price,
                  stock: 100, // Default stock, will be updated if needed
                  // Add other required Product fields with defaults
                }
              : {
                  _id: item.product._id,
                  name: item.product.name,
                  images: item.product.images,
                  price: item.product.price,
                  stock: item.product.stock,
                  // Map other fields
                },
            quantity: item.quantity,
          }));

          // A response started before a local edit must not restore removed rows.
          if (get().items === itemsBeforeFetch) {
            set({ items, lastSyncedAt: Date.now(), isSyncing: false });
          } else {
            set({ isSyncing: false });
          }
        } catch (error) {
          console.error('Failed to fetch cart from backend:', error);
          set({ isSyncing: false });
        }
      },

      /**
       * Sync local cart to backend on login
       * - Fetches backend cart first
       * - Merges local (guest) items into it (local quantity wins on conflict)
       * - Pushes merged items to backend
       * - Updates local state with final merged cart
       */
      syncCart: async () => {
        const isAuthenticated = useAuthStore.getState().isAuthenticated;

        if (!isAuthenticated) {
          return;
        }

        const localItems = get().items;

        if (localItems.length === 0) {
          // No local items — just pull backend cart
          await get().fetchCart();
          return;
        }

        try {
          set({ isSyncing: true });

          // 1. Push every local (guest) item to backend.
          //    Backend merges quantities if the product already exists.
          for (const item of localItems) {
            try {
              await cartService.addToCart({
                product: item.productId,
                quantity: item.quantity,
              });
            } catch (err) {
              console.error(`Failed to sync item ${item.productId} to backend:`, err);
            }
          }

          // 2. Fetch the fully merged cart from the backend and update local state.
          await get().fetchCart();
          set({ lastSyncedAt: Date.now(), isSyncing: false });
        } catch (error) {
          console.error('Failed to sync cart on login:', error);
          // Keep local items intact if sync fails
          set({ isSyncing: false });
        }
      },

      // UI Actions
      toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),
      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),

      // Calculations
      getSubtotal: () => {
        return (get().items || []).reduce(
          (sum, item) => {
            const price = typeof item.product?.price === 'number'
              ? item.product.price
              : (typeof item.product?.displayPrice === 'number' ? item.product.displayPrice : 0);
            const qty = Number(item.quantity) || 0;
            return sum + (price * qty);
          },
          0
        );
      },

      getTax: () => {
        return get().getSubtotal() * 0.0662; // NJ sales tax 6.625%
      },

      getShipping: () => {
        // ── Shipping charge temporarily disabled ──
        // const subtotal = get().getSubtotal();
        // return subtotal >= 150 ? 0 : 12.99;
        return 0;
      },

      getTotal: () => {
        return get().getSubtotal() + get().getTax() + get().getShipping();
      },

      getItemCount: () => {
        return (get().items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      },
    }),
    {
      name: 'patelsales-cart',
      partialize: (state) => ({ items: state.items }),
    }
  )
);

// Subscribe to auth changes to sync cart
useAuthStore.subscribe((state, prevState) => {
  const justLoggedIn = state.isAuthenticated && !prevState.isAuthenticated;
  const justLoggedOut = !state.isAuthenticated && prevState.isAuthenticated;

  if (justLoggedIn) {
    // User just logged in - sync local cart to backend
    useCartStore.getState().syncCart();
  } else if (justLoggedOut) {
    // User logged out - clear cart (keep local for now)
    // You can choose to clear or keep local cart on logout
    // useCartStore.getState().clearCart();
  }
});
